/**
 * FormulaCanvas — empty hint or sortable chip strip for the blocks editor
 * For Media Devoted | Created By Ciro Autiero
 */

import { memo } from 'react';
import type { FC } from 'react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { FormulaSubformula } from '@trafficpulse/shared';
import { readSubformulaRefId } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';

import { useFormulaMetricCatalog } from '../../metric-catalog/FormulaMetricCatalogContext';
import type { ChipToken } from '../blocks.tokens';
import SortableFormulaChip from './SortableFormulaChip';

export interface FormulaCanvasProps {
  chips: readonly ChipToken[];
  subformulas?: FormulaSubformula[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onReorder: (next: ChipToken[]) => void;
}

const FormulaCanvas: FC<FormulaCanvasProps> = ({
  chips,
  subformulas,
  onRemove,
  onClear,
  onReorder,
}) => {
  if (chips.length === 0) return <EmptyCanvas />;
  return (
    <PopulatedCanvas
      chips={chips}
      subformulas={subformulas}
      onRemove={onRemove}
      onClear={onClear}
      onReorder={onReorder}
    />
  );
};

const EmptyCanvas: FC = () => (
  <div className="rounded-md border border-dashed border-border p-4 text-center">
    <p className="text-xs text-muted-foreground">
      Click any block above to start building your formula.
    </p>
  </div>
);

const PopulatedCanvas: FC<FormulaCanvasProps> = ({
  chips,
  subformulas,
  onRemove,
  onClear,
  onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const metadata = useFormulaMetricCatalog();
  const metricNames = new Map((metadata.data?.metrics ?? []).map((m) => [m.id, m.name]));
  const subNames = new Map((subformulas ?? []).map((s) => [s.id, s.name || 'Subformula']));
  const onDragEnd = (event: DragEndEvent) => fireReorder(event, chips, onReorder);
  const ids = chips.map((c) => c.id);
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <p className="mb-2 text-[11px] text-muted-foreground">
        Drag the handle to reorder tokens. Click × to remove.
      </p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <div role="list" className="flex flex-wrap items-center gap-1.5 py-0.5">
            {chips.map((c) => (
              <SortableFormulaChip
                key={c.id}
                id={c.id}
                kind={c.kind}
                display={resolveDisplay(c, metricNames, subNames)}
                onRemove={() => onRemove(c.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="mt-2 flex justify-end">
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={onClear}
          className="h-7 px-2 text-xs text-muted-foreground"
        >
          Clear all
        </Button>
      </div>
    </div>
  );
};

function fireReorder(
  event: DragEndEvent,
  chips: readonly ChipToken[],
  onReorder: (next: ChipToken[]) => void,
): void {
  const { active, over } = event;
  if (!over || active.id === over.id) return;
  const order = chips.map((c) => c.id);
  const oldIndex = order.indexOf(String(active.id));
  const newIndex = order.indexOf(String(over.id));
  if (oldIndex < 0 || newIndex < 0) return;
  onReorder(arrayMove([...chips], oldIndex, newIndex));
}

function resolveDisplay(
  chip: ChipToken,
  metricNames: Map<string, string>,
  subNames: Map<string, string>,
): string {
  if (chip.kind === 'metric') return metricNames.get(chip.value) ?? chip.value;
  if (chip.kind === 'subref') {
    const subId = readSubformulaRefId(chip.value);
    if (subId) return subNames.get(subId) ?? `Subformula ${subId}`;
    return chip.value;
  }
  if (chip.kind === 'op') return opLabel(chip.value);
  if (chip.kind === 'fn') return `${chip.value}(`;
  return chip.value;
}

function opLabel(op: string): string {
  if (op === '*') return '×';
  if (op === '/') return '÷';
  if (op === '-') return '−';
  return op;
}

export default memo(FormulaCanvas);
